/**
 * Catalogue des expériences A/B (B20).
 *
 * Chaque entrée mappe une clé de feature flag PostHog (à créer côté dashboard
 * en mode "experiment" multivarié) avec ses variantes et l'objectif de conversion.
 *
 * Workflow :
 *   1. Créer le flag PostHog avec la `key` ci-dessous + les variantes listées.
 *   2. Côté composant : `const { variant } = useExperiment(EXPERIMENTS.ctaWording.key, 'control')`
 *   3. Au moment de la conversion : `trackExperimentConversion(key, variant, goal)`
 *
 * Convention de variante : 'control' = version actuelle (référence).
 */

export interface ExperimentDef {
  key: string;
  hypothesis: string;
  variants: string[];
  goalEvent: string;
  surface: string;
}

export const EXPERIMENTS = {
  ctaWording: {
    key: 'exp_cta_wording',
    hypothesis: 'Un CTA orienté bénéfice immédiat augmente le clic vers le funnel.',
    variants: ['control', 'benefit', 'urgent'],
    goalEvent: 'funnel_start',
    surface: 'Hero pages audiences',
  },
  diagnosticStepsCount: {
    key: 'exp_diagnostic_steps',
    hypothesis: 'Réduire le diagnostic de 4 à 3 étapes augmente le taux de complétion.',
    variants: ['control', 'short'],
    goalEvent: 'submit_success',
    surface: 'DiagnosticFunnel',
  },
  leadFormFields: {
    key: 'exp_leadform_fields',
    hypothesis: 'Demander le téléphone en optionnel (vs requis) augmente la soumission.',
    variants: ['control', 'phone_optional'],
    goalEvent: 'submit_success',
    surface: 'LeadForm / Step contact',
  },
  socialProofPlacement: {
    key: 'exp_social_proof',
    hypothesis: 'Afficher la preuve sociale au-dessus du formulaire augmente la confiance.',
    variants: ['control', 'above_form'],
    goalEvent: 'submit_start',
    surface: 'Pages outils',
  },
  pricingTransparency: {
    key: 'exp_pricing_transparency',
    hypothesis: 'Afficher une fourchette de prime estimée tôt augmente l\'engagement.',
    variants: ['control', 'early_estimate'],
    goalEvent: 'step_complete',
    surface: 'DiagnosticFunnel step 1',
  },
  heroVisual: {
    key: 'exp_hero_visual',
    hypothesis: 'Une illustration produit vs photo lifestyle change le taux de scroll/clic.',
    variants: ['control', 'illustration'],
    goalEvent: 'funnel_start',
    surface: 'Home + audiences',
  },
  exitIntentOffer: {
    key: 'exp_exit_intent',
    hypothesis: 'Un rappel à la sortie réduit l\'abandon du funnel.',
    variants: ['control', 'exit_modal'],
    goalEvent: 'funnel_resume',
    surface: 'Funnels',
  },
  trustBadges: {
    key: 'exp_trust_badges',
    hypothesis: 'Des badges (RGE, agréé CEE) près du CTA augmentent la conversion.',
    variants: ['control', 'badges'],
    goalEvent: 'submit_success',
    surface: 'Footer formulaire',
  },
  mobileStickyCta: {
    key: 'exp_mobile_sticky_cta',
    hypothesis: 'Un CTA sticky en bas sur mobile augmente l\'entrée en funnel.',
    variants: ['control', 'sticky'],
    goalEvent: 'funnel_start',
    surface: 'Mobile — pages audiences',
  },
  newsletterIncentive: {
    key: 'exp_newsletter_incentive',
    hypothesis: 'Offrir un guide PDF augmente l\'opt-in newsletter (consenti).',
    variants: ['control', 'lead_magnet'],
    goalEvent: 'submit_success',
    surface: 'Blocs newsletter',
  },
} as const satisfies Record<string, ExperimentDef>;

export type ExperimentKey = keyof typeof EXPERIMENTS;
